import React, { useState, useEffect } from "react";

import { connect } from "react-redux";
import { useIntl } from "react-intl";

import { faSignInAlt, faSignOutAlt, faBox } from "@fortawesome/free-solid-svg-icons";

import Card from "../../UI/Card/Card";
import UserHz from "../../User/UserHz";
import LoginInput from "../Login/LoginInput";
import CloseOnSuccessModalWizard, { ModalSize } from "../../UI/Modal/CloseOnSuccessModalWizard";

import * as SandBoxAuthenticationActions from "../../../middleware/Store/Actions/SandBoxAuthenticationActions";
import * as AuthenticationUtils from "../../Utils/AuthenticationUtils";
import * as ContactUtils from "../../Utils/ContactUtils";
import * as ErrorUtils from "../../Utils/ErrorUtils";

import LogService from "../../../middleware/CommonSdk/LogService";

const LOG_TAG = "SANDBOX_LOGIN";

const SandBoxLogin = (props) => {
    const intl = useIntl();
    const [modal, setModal] = useState(null);

    useEffect(() => {
        LogService.trace(LOG_TAG, "use effect authentication");
        if (modal) {
            updateLoginModal();
        }

    }, [props.account.authentication]);

    const resetEnvironment = () => {
        props.clearEnvironment();
        props.clearAuthentication();
    }

    const handleEmailChange = (email) => {
        if (email && email.length > 0) {
            resetEnvironment();
            return props.getUserEnvironment(email)
                .then((data) => {
                    return props.getUserAuthentication(email);
                })
                .then((data) => {
                    return Promise.resolve();
                })
                .catch((errorPayload) => {
                    return Promise.reject(errorPayload);
                });
        } else {
            resetEnvironment();
            return Promise.resolve();
        }
    }

    const handleServerChange = (email, serverIndex) => {
        props.selectUserEnvironment(serverIndex);
        return props.getUserAuthentication(email)
            .then((data) => {
                return Promise.resolve();
            })
            .catch((errorPayload) => {
                return Promise.reject(errorPayload);
            });
    }

    const handleAuthenticationChange = (authenticationIndex) => {
        props.selectUserAuthentication(authenticationIndex);
    }

    const handleLogin = (data, handleOnSuccess, handleOnFailed) => {
        LogService.trace(LOG_TAG, "handleLogin");

        if (AuthenticationUtils.isRainbowAuthentication(props.account)) {
            return props.login(data)
                .then(() => {
                    return Promise.resolve({ action: handleOnSuccess });
                })
                .catch((errorPayload) => {
                    return Promise.reject({ errorPayload: errorPayload, action: () => handleOnFailed(intl.messages.sandBoxLoginConnectFailedMessage) });
                });
        }

        LogService.error(LOG_TAG, "handleLogin", "authentication not handled");
        return Promise.reject({ errorPayload: ErrorUtils.parseErrorAndReturnPayload("Authentication not handled") });
    }

    const handleLoginLogout = (logout) => {
        LogService.trace(LOG_TAG, "handleLoginLogout");
        if (logout) {
            LogService.debug(LOG_TAG, "handleLoginLogout", "logout");
            setModal(null);
            props.logout();
        } else {
            LogService.debug(LOG_TAG, "handleLoginLogout", "login");
            updateLoginModal();
        }
    };

    const updateLoginModal = () => {
        LogService.trace(LOG_TAG, "updateLoginModal");

        const getComponent = (handleOnSuccess, handleOnFailed) => {
            const enableLoginButton = AuthenticationUtils.isEnvironmentAvailable(props.account) && AuthenticationUtils.isAuthenticationAvailable(props.account);

            let lostPasswordUrl = null;
            let selectedServerUrl = AuthenticationUtils.getSelectedServerUrl(props.account);
            if (!selectedServerUrl) {
                selectedServerUrl = AuthenticationUtils.getBaseServerUrl(props.account);
            }
            if (selectedServerUrl) {
                lostPasswordUrl = new URL("login", selectedServerUrl);
                lostPasswordUrl.host = AuthenticationUtils.WebClientPrefix + lostPasswordUrl.host; //web. valid only on .com
            }

            return (
                <LoginInput
                    loginMessage={intl.messages.sandBoxLoginConnectMessage}
                    server={props.account.environment}
                    authentication={props.account.authentication}
                    selectedServerIndex={props.account.selectedServerIndex}
                    enableLoginButton={enableLoginButton}
                    onLoginEmailChange={handleEmailChange}
                    onServerChange={handleServerChange}
                    onAuthenticationChange={handleAuthenticationChange}
                    onLogin={(data) => handleLogin(data, handleOnSuccess, handleOnFailed)}
                    forgotPasswordUrl={lostPasswordUrl}
                />
            );
        }
        setModal(
            <CloseOnSuccessModalWizard
                title={intl.messages.sandBoxLoginTitle}
                icon={faBox}
                iconColor="green"
                getComponent={getComponent}
                modalSize={ModalSize.Small}
            />
        );
    };

    LogService.trace(LOG_TAG, "render");
    const userName = ContactUtils.getDisplayName(props.sandBoxUser);
    const companyName = ContactUtils.getCompanyName(props.sandBoxUser);
    const avatar = ContactUtils.getAvatar(props.sandBoxUser);

    let iconLogin = faSignInAlt;
    let labelLogin = intl.messages.loginTooltip;
    let isAuthenticated = props.sandBoxUser != null;
    if (isAuthenticated) {
        iconLogin = faSignOutAlt;
        labelLogin = intl.messages.logoutTooltip;
    }

    let content = <div className="panel bg-fog mb-3">{intl.messages.sandBoxLoginMessage}</div>;
    if (isAuthenticated) {
        content = <UserHz name={userName} title={companyName} avatar={avatar} />;
    }

    return (           
        <Card
            title={intl.messages.sandBoxLoginTitle}
            icon={iconLogin}
            label={labelLogin}
            onClick={() => handleLoginLogout(isAuthenticated)}
        >
            {content}
            {modal}
        </Card>
    );
}

const mapStateToProps = state => {
    return {
        account: state.sandBoxAuthentication.account,
        sandBoxUser: state.sandBoxContacts.user
    };
};

const mapDispatchToProps = dispatch => {
    return {
        getUserAuthentication: (email) => dispatch(SandBoxAuthenticationActions.getUserAuthentication(email)),
        getUserEnvironment: (email) => dispatch(SandBoxAuthenticationActions.getUserEnvironment(email)),
        selectUserEnvironment: (index) => dispatch(SandBoxAuthenticationActions.selectUserEnvironment(index)),
        selectUserAuthentication: (index) => dispatch(SandBoxAuthenticationActions.selectUserAuthentication(index)),
        login: (data) => dispatch(SandBoxAuthenticationActions.login(data)),
        logout: () => dispatch(SandBoxAuthenticationActions.logout()),
        clearEnvironment: () => dispatch(SandBoxAuthenticationActions.clearEnvironment()),
        clearAuthentication: () => dispatch(SandBoxAuthenticationActions.clearAuthentication())
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(SandBoxLogin);